import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { DateRangePicker } from 'react-dates';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import styles from './DateRange.module.scss';
import moment from 'moment';
import useLocalStorage from '../../../hooks/useLocalStorage';

const presets = [
  {
    text: 'Today',
    start: moment().startOf('day'),
    end: moment().endOf('day'),
  },
  {
    text: 'This Week',
    start: moment().startOf('week'),
    end: moment().endOf('week'),
  },
  {
    text: 'This Month',
    start: moment().startOf('month').startOf('day'),
    end: moment().endOf('day'),
  },
  {
    text: 'Last Month',
    start: moment().subtract(1, 'month').startOf('month').startOf('day'),
    end: moment().subtract(1, 'month').endOf('month'),
  },
  {
    text: 'Last 7 Days',
    start: moment().subtract(7, 'days').startOf('day'),
    end: moment().endOf('day'),
  },
  {
    text: 'Last 14 Days',
    start: moment().subtract(14, 'days').startOf('day'),
    end: moment().endOf('day'),
  },
  {
    text: 'Last 30 Days',
    start: moment().subtract(30, 'days').startOf('day'),
    end: moment().endOf('day'),
  },
];

function DateRange(props) {
  const { startDateStorageKey, endDateStorageKey, limitToMonth, initialStartDate, initialEndDate, minDate, maxDate } =
    props;

  const [startDate, setStartDate] = useLocalStorage(
    startDateStorageKey,
    initialStartDate ? moment(initialStartDate) : moment().startOf('day'),
    (value) => {
      const date = moment(new Date(value));
      return date.isValid() ? date : moment().startOf('day');
    },
  );

  const [endDate, setEndDate] = useLocalStorage(
    endDateStorageKey,
    initialEndDate ? moment(initialEndDate) : moment().endOf('day'),
    (value) => {
      const date = moment(new Date(value));
      return date.isValid() && date.isAfter(startDate) ? date : moment(startDate).endOf('day');
    },
  );

  const [focused, setFocused] = useState(null);

  useEffect(() => {
    if (!maxDate) return;

    if (startDate.isAfter(maxDate)) {
      setStartDate(moment(maxDate).startOf('day'));
      setEndDate(moment(maxDate).endOf('day'));
    } else if (endDate.isAfter(maxDate)) {
      setEndDate(moment(maxDate).endOf('day'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = (startDate, endDate) => {
    setStartDate(startDate.startOf('day'));
    setEndDate(endDate.endOf('day'));
    props.onChange(startDate, endDate);
  };

  /* Fixes a unique bug when the end date is past the start date */
  const handleThisWeekPresetSelection = ({ start, end }) => {
    if (!maxDate) {
      handleSelect(start, end);
      return;
    }

    const isEndDateSaturday = end.weekday() === 6;
    if (start.isAfter(end) && end.isSameOrAfter(maxDate) && isEndDateSaturday) {
      const startDateToSet = moment(end).subtract(6, 'days').startOf('day');
      const endDateToSet = moment(end).endOf('day');
      handleSelect(startDateToSet, endDateToSet);
      return;
    }

    handleSelect(start, end);
  };

  const renderDatePresets = () => {
    return (
      <div className={styles.presets}>
        {presets.map(({ text, start, end: presetEnd }) => {
          const end = moment.min([presetEnd, maxDate ? moment(maxDate).subtract(1).endOf('day') : moment()]);
          if (text === 'Today' && maxDate <= moment().endOf('day')) {
            return <Fragment key={text} />;
          }

          const isSelected = start.isSame(startDate, 'day') && end.isSame(endDate, 'day');
          return (
            <button
              className={isSelected ? styles.selected_button : styles.button}
              key={text}
              onClick={() => {
                setFocused(null);
                if (text === 'This Week') {
                  handleThisWeekPresetSelection({ start, end });
                  return;
                }

                handleSelect(start, end);
              }}
            >
              {text}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <DateRangePicker
      startDate={startDate}
      startDateId="start_date"
      endDate={endDate}
      endDateId="end_date"
      focusedInput={focused}
      onFocusChange={setFocused}
      onDatesChange={(state) => {
        setStartDate(state.startDate);
        setEndDate(state.endDate);
      }}
      noBorder={true}
      renderCalendarInfo={renderDatePresets}
      minimumNights={0}
      onClose={(state) => {
        if (state.startDate && state.endDate) {
          let endDate = state.endDate;
          const diff = endDate.diff(state.startDate, 'months');
          if (diff > 0 && limitToMonth) {
            endDate = moment(state.startDate).add(1, 'months').endOf('day');
            setEndDate(endDate);
          }
          handleSelect(moment(state.startDate).startOf('day'), endDate);
        } else if (state.startDate) {
          const endDate = moment(startDate).endOf('day');
          handleSelect(moment(state.startDate).startOf('day'), endDate);
        }
      }}
      isOutsideRange={(date) => {
        if (minDate && date.isBefore(minDate)) {
          return true;
        }

        if (maxDate && date.isAfter(maxDate)) {
          return true;
        }

        if (date.isAfter(moment().endOf('day'))) {
          return true;
        }

        if (focused === 'endDate' && limitToMonth) {
          const diff = date.diff(startDate.endOf('day'), 'months');
          return diff !== 0;
        }

        return false;
      }}
    />
  );
}

export default DateRange;

DateRange.defaultProps = {
  startDateStorageKey: 'startDate',
  endDateStorageKey: 'endDate',
  limitToMonth: true,
};

DateRange.propTypes = {
  onChange: PropTypes.func.isRequired,
  startDateStorageKey: PropTypes.string,
  endDateStorageKey: PropTypes.string,
  limitToMonth: PropTypes.bool,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
};
